import React, { useState, useEffect } from "react";
import { Card, Button, Radio } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Constant from "../../../global/Constant.ts";
import { useSelector, useDispatch } from "react-redux";
import { setLoading } from "../../../redux/globalSlice.ts";
import assesmentService from "../../../services/assessmentService.ts";
import { RootState } from "../../../redux/store.ts";
import { decryptData } from "../../../utils/decryptData.ts";


async function decodeCorrectAnswer(encryptedAnswer, base64Key) {
  if (!encryptedAnswer) return "";

  const [ivBase64, encryptedBase64, authTagBase64] = encryptedAnswer.split(":");
  if (!ivBase64 || !encryptedBase64 || !authTagBase64) return "";

  try {
    const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
    const ciphertext = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
    const authTag = Uint8Array.from(atob(authTagBase64), c => c.charCodeAt(0));

    const encryptedData = new Uint8Array(ciphertext.length + authTag.length);
    encryptedData.set(ciphertext);
    encryptedData.set(authTag, ciphertext.length);

    const rawKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      rawKey,
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      cryptoKey,
      encryptedData
    );

    return new TextDecoder().decode(decrypted);
  } catch (err) {
    console.error("Decryption failed:", err);
    return "";
  }
}


function hexToBase64(hex) {
  return btoa(
    hex.match(/\w{2}/g)
       .map((byte) => String.fromCharCode(parseInt(byte, 16)))
       .join('')
  );
}


const AssessmentQuestion = () => {
  const [assessment, setAssessment] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [lastSubmittedAnswers, setLastSubmittedAnswers] = useState({}); // Track last submitted answers
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const isResume = searchParams.get("resume") === "true";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loggedUserDetails = useSelector(
    (state: RootState) => state.user.userDetails
  );
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    getAssessmentDataByID();
  }, []);

  useEffect(() => {
    if (assessment.length > 0 && isResume) {
      fetchUserAnswers();
    }
  }, [assessment, isResume]);

  const getAssessmentDataByID = async () => {
    try {
      dispatch(setLoading(true));
      if (assessmentId) {
        const response = await assesmentService.getAssessmentDataByID(
          Constant?.assessment,
          assessmentId
        );
        setAssessment(response || []);
      }
    } catch (error) {
      console.error("Error fetching Assessment:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchUserAnswers = async () => {
    try {
      dispatch(setLoading(true));
      const response = await assesmentService.getAssessmentSummaryData(
        Constant.assessmentanswers,
        assessmentId,
        loggedUserDetails?.UserID
      );

      const encryptedData = response?.encryptedData;
  
      if (!encryptedData) {
        return;
      }
  
      if (!process.env.REACT_APP_ENCRYPTION_KEY) {
        return;
      }
  
      const decrypted = await decryptData(
        encryptedData,
        process.env.REACT_APP_ENCRYPTION_KEY
      );
  
      let parsedAnswers;
      if (Array.isArray(decrypted)) {
        parsedAnswers = decrypted;
      } else {
          parsedAnswers = JSON.parse(decrypted);

      }
        const userAnswers = parsedAnswers.reduce((acc, answer) => {
          acc[answer.AssessmentQuestionID] = answer.UserAnswer;
          return acc;
        }, {});

        setSelectedAnswers(userAnswers);
        setLastSubmittedAnswers(userAnswers); // Initialize with fetched answers

        const firstUnansweredIndex = assessment.findIndex(
          (question) => !userAnswers[question.AssessmentQuestionID]
        );
        setCurrentQuestionIndex(
          firstUnansweredIndex !== -1
            ? firstUnansweredIndex
            : assessment.length - 1
        );
    } catch (error) {
      console.error("Error fetching user answers:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const submitAnswer = async (questionId, answer) => {
    if (lastSubmittedAnswers[questionId] === answer) return Promise.resolve();
  
    const currentSubmitQuestion = assessment.find(
      (q) => q.AssessmentQuestionID === questionId
    );
    if (!currentSubmitQuestion) return Promise.resolve();
  
    const base64Key = hexToBase64(process.env.REACT_APP_ENCRYPTION_KEY);
    const decodedCorrectAnswer = await decodeCorrectAnswer(
      currentSubmitQuestion.CorrectAnswer,
      base64Key
    );
  
    const payload = {
      AssessmentID: assessmentId,
      UserID: loggedUserDetails?.UserID,
      QuestionIndex: currentQuestionIndex + 1,
      Answers: [
        {
          AssessmentQuestionID: questionId,
          UserAnswer: answer,
          Verified: btoa(String(answer === decodedCorrectAnswer)),
        },
      ],
    };

    try {
      const response = await assesmentService.submitAssessmentDataByID(
        Constant.assessmentanswers,
        payload
      );
      // Update last submitted answer
      setLastSubmittedAnswers((prev) => ({
        ...prev,
        [questionId]: answer,
      }));
      return response;
    } catch (error) {
      console.error("Error submitting answer:", error);
      throw error;
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < assessment.length - 1) {
      const currentSubmitQuestion = assessment[currentQuestionIndex];
      const currentAnswer =
        selectedAnswers[currentSubmitQuestion.AssessmentQuestionID];
      submitAnswer(currentSubmitQuestion.AssessmentQuestionID, currentAnswer);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const currentSubmitQuestion = assessment[currentQuestionIndex];
      const currentAnswer =
        selectedAnswers[currentSubmitQuestion.AssessmentQuestionID];
      submitAnswer(currentSubmitQuestion.AssessmentQuestionID, currentAnswer);
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      dispatch(setLoading(true));

      const currentSubmitQuestion = assessment[currentQuestionIndex];
      const currentAnswer =
        selectedAnswers[currentSubmitQuestion.AssessmentQuestionID];

      // Wait for the submission to complete
      await submitAnswer(
        currentSubmitQuestion.AssessmentQuestionID,
        currentAnswer
      );

      navigate("/assessment-summary", {
        state: {
          assessmentId: assessmentId,
          userId: loggedUserDetails?.UserID,
        },
      });
    } catch (error) {
      console.error("Error submitting assessment:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAnswerChange = (e) => {
    const currentQuestionItem = assessment[currentQuestionIndex];
    const newAnswer = e.target.value;

    setSelectedAnswers((prev) => ({
      ...prev,
      [currentQuestionItem.AssessmentQuestionID]: newAnswer,
    }));

    // Only submit if answer has changed
    if (
      lastSubmittedAnswers[currentQuestionItem.AssessmentQuestionID] !==
      newAnswer
    ) {
      submitAnswer(currentQuestionItem.AssessmentQuestionID, newAnswer);
    }
  };

  if (!assessment.length) {
    return (
      <div className="vm__assessment vm__h-100">
        <div className="vm__container vm__h-100">
          {/* Loading or empty state */}
        </div>
      </div>
    );
  }

  const currentQuestion = assessment[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === assessment.length - 1;
  const hasAnswer =
    selectedAnswers[currentQuestion.AssessmentQuestionID] !== undefined;

  return (
    <div className="vm__assessment vm__h-100">
      <div className="vm__container vm__h-100">
        <div className="vm__d-flex vm__flex-column vm__h-100">
          <div className="vm__overflow-auto">
            <Card className="vm__assessment__card vm__w-100">
              <section className="vm__assessment__card__header">
                <span>Question {currentQuestionIndex + 1}</span>
                <h4>{currentQuestion?.QuestionText}</h4>
              </section>

              <div className="vm__radio-group">
                <Radio.Group
                  onChange={handleAnswerChange}
                  value={selectedAnswers[currentQuestion.AssessmentQuestionID]}
                  className="vm__d-flex vm__flex-column vm__gap-3"
                >
                  {[
                    currentQuestion?.Answer1,
                    currentQuestion?.Answer2,
                    currentQuestion?.Answer3,
                    currentQuestion?.Answer4,
                  ].map((option, index) => (
                    <Radio key={index} value={option} className="vm__radio">
                      {option}
                    </Radio>
                  ))}
                </Radio.Group>
              </div>

              <section className="vm__assessment__card__actions">
                <div className="vm__assessment__card__actions-left">
                  {/* Optional exit button */}
                </div>
                <div className="vm__assessment__card__actions-right">
                  <Button
                    type="default"
                    className="vm__button"
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                    icon={<LeftOutlined />}
                  >
                    Back
                  </Button>
                  {isLastQuestion ? (
                    <Button
                      type="primary"
                      onClick={handleSubmit}
                      disabled={!hasAnswer}
                    >
                      Submit Training
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={handleNextQuestion}
                      disabled={!hasAnswer}
                      icon={<RightOutlined />}
                    >
                      Next Question
                    </Button>
                  )}
                </div>
              </section>
            </Card>
          </div>
          <div className="vm__flex-auto">
            <p className="vm__assessment__footer">
              Question {currentQuestionIndex + 1} of {assessment.length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentQuestion;
